import { isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, inject, Injector, OnInit, PLATFORM_ID, REQUEST } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';

import { NgxPermissionsService } from 'ngx-permissions';
import { EMPTY, from } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

import { ErrorService } from '@core/services/error.service';
import { VersionService } from '@core/services/version.service';
import { cleanServiceWorkerCache, getLanguage } from '@core/utils';
import { ErrorDialogComponent } from '@shared/dialogs/error-dialog/error-dialog.component';
import { NewFeaturesDialogComponent } from '@shared/dialogs/new-features-dialog/new-features-dialog.component';
import { TechnicalWorkDialogComponent } from '@shared/dialogs/technical-work-dialog/technical-work-dialog.component';
import { environment as env } from '@environments/environment';

import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'eb-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
  private readonly errorService = inject(ErrorService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly permissionService = inject(NgxPermissionsService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly request = inject(REQUEST, { optional: true });
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  constructor() {
    if (this.isBrowser) {
      this.removePwaCache();
      this.initLocale();
    } else {
      this.initServerLocale();
    }
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.setPermissions();
    this.showError();
    this.showTechnicalWorkDialog();
    this.refreshPwa();
    this.checkVersion();
  }

  private checkVersion(): void {
    const version = this.injector.get(VersionService);

    version.setVersion(env.version, '');
  }

  private initLocale(): void {
    this.translate.addLangs(['ru', 'en', 'zh']);
    const defaultLanguage = getLanguage();
    this.translate.setFallbackLang(defaultLanguage || 'ru');
    this.translate.use(defaultLanguage.match(/ru|en|zh/) ? defaultLanguage : 'ru');
  }

  private initServerLocale(): void {
    this.translate.addLangs(['ru', 'en', 'zh']);
    const url = this.request?.url ?? '';
    const pathname = url.startsWith('http') ? new URL(url).pathname : url;
    const language = pathname.match(/^\/(ru|en|zh)(?:\/|$)/)?.[1] ?? 'ru';

    this.translate.setFallbackLang(language);
    this.translate.use(language);
  }

  private setPermissions(): void {
    const permissions = JSON.parse(sessionStorage.getItem('permissions'));
    if (permissions) {
      this.permissionService.loadPermissions(permissions);
    }
  }

  private showError(): void {
    this.errorService.show$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ title, body }) => {
      this.showErrorDialog(title, body);
    });
  }

  private showTechnicalWorkDialog(): void {
    this.errorService.serverError$
      .pipe(
        switchMap(({ title, body }) => {
          if (this.dialog.getDialogById('technicalWork')) {
            return EMPTY;
          }
          this.errorService.hasTechnicalDialog = true;
          const dialogConfig = new MatDialogConfig();
          dialogConfig.data = { title, body };
          dialogConfig.maxWidth = '90vw';
          dialogConfig.id = 'technicalWork';
          return this.dialog.open(TechnicalWorkDialogComponent, dialogConfig).afterOpened();
        }),
        finalize(() => (this.errorService.hasTechnicalDialog = false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => (this.errorService.hasTechnicalDialog = false));
  }

  private refreshPwa(): void {
    if (this.swUpdate.isEnabled) {
      from(this.swUpdate.activateUpdate())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(upd => {
          console.log('New app version successful updated', upd);
        });
      this.swUpdate.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(upd => {
        switch (upd.type) {
          case 'VERSION_DETECTED':
            console.log(`Downloading new app version: ${upd.version.hash}`);
            break;
          case 'VERSION_READY':
            console.log(`Current app version: ${upd.currentVersion.hash}`);
            console.log(`New app version ready for use: ${upd.latestVersion.hash}`);
            if (!this.dialog.getDialogById('new-features') && !env.isTestSite) {
              this.dialog.open(NewFeaturesDialogComponent, {
                id: 'new-features',
              });
            }
            break;
          case 'VERSION_INSTALLATION_FAILED':
            console.log(`Failed to install app version '${upd.version.hash}': ${upd.error}`);
            break;
        }
      });
      this.swUpdate
        .checkForUpdate()
        .then(updateFound =>
          console.log(updateFound ? 'A new version is available.' : 'Already on the latest version.')
        )
        .catch(error => {
          console.error('Could not check for app updates', error);
        });
    }
  }

  private showErrorDialog(title: string, body: string): void {
    if (this.dialog.getDialogById('error')) {
      return;
    }
    this.errorService.hasDialog = true;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { title, body };
    dialogConfig.maxWidth = '90vw';
    dialogConfig.id = 'error';
    this.dialog
      .open(ErrorDialogComponent, dialogConfig)
      .afterOpened()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.errorService.hasDialog = false));
  }

  // TODO: implement for clean pwa cache
  private removePwaCache(): void {
    if ('caches' in window && env.production) {
      const lastVersion = localStorage.getItem('version');
      if (!lastVersion) {
        cleanServiceWorkerCache();
        localStorage.setItem('version', env.appVersion);
      } else if (lastVersion !== env.appVersion) {
        localStorage.setItem('version', env.appVersion);
        cleanServiceWorkerCache();
      }
    }
  }
}
